import {useCallback, useState} from 'react';
import {ConfirmationModal} from '@/matic/features/_shared/components/ConfirmationModal';
import {getFilesApi} from '../api/files-api';
import {useFileTags} from '../hooks/useFileTags';
import {useFileMutations} from '../hooks/useFileMutations';
import {useFilePermissions} from '../hooks/useFilePermissions';
import type {ManagedFile} from '../types/files-types';
import {FilePreviewPanel} from './FilePreviewPanel';

type Props = {
  file: ManagedFile;
  onClose: () => void;
  /** Relay-sourced files show a "From: <email>" badge that navigates here. */
  onNavigateToThread?: (threadId: string) => void;
  /** Adds a "Chat with this file" header action when provided. */
  onChatWithFile?: (file: ManagedFile) => void;
  /** Overrides the default navigate-to-Library-record behavior of an
   *  association chip (e.g. to append to the current scope instead of
   *  leaving the page). Omit to use FilePreviewPanel's default navigation. */
  onNavigateToAssociation?: (typeId: string, recordId: string | null) => void;
  hideTitleHeader?: boolean;
  initialPage?: number;
  scrollKey?: number;
};

/**
 * The "fully wired" file viewer: `FilePreviewPanel` plus the permission
 * check, mutations, category list, and delete-confirmation flow that make
 * Delete / rename / recategorize / associations actually work. Any RHS
 * surface that lets a user *manage* (not just preview) a file should mount
 * this rather than wiring `FilePreviewPanel` up by hand — per
 * `uxConventions.md` §4.2, previews are a shared contract, not a per-feature
 * reimplementation.
 *
 * Known drift: this exact wiring still exists hand-rolled in two other
 * places — `LibraryStream.tsx`'s `managedFile` case and `LibraryFilesTab`'s
 * standalone chrome (which absorbed the old `LibraryFilesBrowserView`) —
 * neither has been migrated onto this component yet (left as-is to avoid
 * regression risk in files unrelated to the change that introduced this
 * component). Migrate them here next time either is touched; see
 * `featureInventory.md`'s drift catalog.
 */
export function ManagedFilePreviewPanel({
  file,
  onClose,
  onNavigateToThread,
  onChatWithFile,
  onNavigateToAssociation,
  hideTitleHeader,
  initialPage,
  scrollKey,
}: Props) {
  const {canManage} = useFilePermissions();
  const {data: categoriesData} = useFileTags();
  const mutations = useFileMutations();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDownload = useCallback((target: ManagedFile) => {
    void (async () => {
      const {url} = await getFilesApi().getContentUrl(target.id);
      globalThis.open(url, '_blank');
    })();
  }, []);

  const handleConfirmDelete = useCallback(() => {
    mutations.softDeleteFile.mutate(file.id, {
      onSuccess() {
        setConfirmingDelete(false);
        onClose();
      },
    });
  }, [file.id, mutations.softDeleteFile, onClose]);

  return (
    <>
      <FilePreviewPanel
        file={file}
        onClose={onClose}
        onDownload={handleDownload}
        hideTitleHeader={hideTitleHeader}
        canManage={canManage}
        categories={categoriesData ?? []}
        onUpdateFile={(target, input) => {
          mutations.updateFile.mutate({id: target.id, input});
        }}
        onCreateCategory={async (name, sortOrder) =>
          mutations.createCategory.mutateAsync({name, sortOrder})
        }
        isCreateCategoryPending={mutations.createCategory.isPending}
        onDelete={() => {
          setConfirmingDelete(true);
        }}
        onAddAssociation={(objectTypeId, objectRecordId) => {
          mutations.addAssociation.mutate({
            fileId: file.id,
            objectTypeId,
            objectRecordId,
          });
        }}
        onRemoveAssociation={(assocId) => {
          mutations.removeAssociation.mutate({fileId: file.id, assocId});
        }}
        onNavigateToAssociation={onNavigateToAssociation}
        onNavigateToThread={onNavigateToThread}
        onChatWithFile={onChatWithFile}
        isAssociationBusy={
          mutations.addAssociation.isPending ||
          mutations.removeAssociation.isPending
        }
        initialPage={initialPage}
        scrollKey={scrollKey}
      />

      <ConfirmationModal
        open={confirmingDelete}
        title="Delete File"
        description={`Are you sure you want to delete "${file.name}"? This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmingDelete(false);
        }}
        isLoading={mutations.softDeleteFile.isPending}
      />
    </>
  );
}
